import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useCanvas } from '../../hooks/useCanvas';
import usePropertyHandlers from '../../hooks/usePropertyHandlers';
import MediaSelectorModal from '../Canvas/PropertiesPanel/MediaSelectorModal';
import { ItemThumb, KeyboardHint } from './LiteSharedComponents';
import ExpandedFields from './ExpandedFields';
import ExpandedControls from './ExpandedControls';
import AiLiteFields from './AiLiteFields';
import {
  collectDisplayItems,
  collectLayerProperties,
  getRotatedSecondaryItems,
  getProgressMetrics,
  getQuestionText,
} from '../../utils/liteDisplayUtils';
import { findAdjacentMediaTime } from '../../utils/liteNavigationUtils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
 * Helper: build opts for standalone media elements
 * ═══════════════════════════════════════════════════════════════════════════ */
function buildStandaloneOpts(canvasEl) {
  const opts = {};
  const sprite = canvasEl.pixiSprite;
  const texW = sprite?.texture?.source?.width || sprite?.texture?.width || 0;
  const texH = sprite?.texture?.source?.height || sprite?.texture?.height || 0;
  const w = canvasEl.componentData?.dimensions?.width || canvasEl.componentData?.width || (texW > 0 ? texW : null) || canvasEl.width || null;
  const h = canvasEl.componentData?.dimensions?.height || canvasEl.componentData?.height || (texH > 0 ? texH : null) || canvasEl.height || null;
  if (w) opts.targetWidth = w;
  if (h) opts.targetHeight = h;
  const FPS = 25;
  const dur = canvasEl.componentData?.duration || (canvasEl.componentData?.frameDuration ? canvasEl.componentData.frameDuration / FPS : null) || canvasEl.timeline?.duration || null;
  if (dur) opts.maxDuration = dur;
  return opts;
}

/**
 * LitePropertiesPanel
 *
 * Sits below the Pixi stage in the LiteViewPage.
 * Two modes:
 *   1. Simple — focused item thumbnail + question prompt + change button
 *   2. Expanded ("Show all properties") — Fields + Controls per layer
 *
 * Both modes support arrow-key and mouse-wheel navigation through layers,
 * jumping to the next/prev time segment at boundaries.
 */
const LitePropertiesPanel = ({ replaceElementMedia, onNavigate }) => {
  const { canvasElements, currentTime, isPlaying, setCurrentTime } = useCanvas();
  const { handlePropertyChange, handleSliderStart, handleSliderEnd } = usePropertyHandlers();
  const mediaSelectorRef = useRef(null);

  const [focusedIndex, setFocusedIndex] = useState(0);
  const [showAllProperties, setShowAllProperties] = useState(false);
  const pendingJumpRef = useRef(null);
  const [editingText, setEditingText] = useState(null);
  // Track which field's font picker is open (by fieldIndex); null = none open
  const [openFontFieldIndex, setOpenFontFieldIndex] = useState(null);
  const expandedContainerRef = useRef(null);

  // Freeze displayTime while playing
  const [displayTime, setDisplayTime] = useState(currentTime);
  useEffect(() => { if (!isPlaying) setDisplayTime(currentTime); }, [isPlaying, currentTime]);

  // Collect items for both modes
  const displayItems = useMemo(
    () => collectDisplayItems(canvasElements, displayTime),
    [canvasElements, displayTime],
  );
  const layerProperties = useMemo(
    () => collectLayerProperties(canvasElements, displayTime),
    [canvasElements, displayTime],
  );

  const navTotal = showAllProperties ? layerProperties.length : displayItems.length;

  // Reset focused index on item changes or honour pending jump.
  // `displayTime` is included so that pending jumps are processed as soon as the
  // time actually propagates, even when navTotal stays numerically the same.
  useEffect(() => {
    if (pendingJumpRef.current === 'end' && navTotal > 0) {
      setFocusedIndex(navTotal - 1);
      pendingJumpRef.current = null;
    } else if (pendingJumpRef.current === 'start') {
      setFocusedIndex(0);
      pendingJumpRef.current = null;
    } else if (focusedIndex >= navTotal) {
      setFocusedIndex(Math.max(0, navTotal - 1));
    }
  }, [navTotal, focusedIndex, displayTime]);

  // Simple-mode derived data
  const total = displayItems.length;
  const primaryItem = displayItems[focusedIndex] || null;
  const secondaryItems = useMemo(
    () => getRotatedSecondaryItems(displayItems, focusedIndex),
    [displayItems, focusedIndex],
  );
  const { thumbPct, topPct } = getProgressMetrics(navTotal, focusedIndex);
  const questionText = useMemo(() => getQuestionText(primaryItem), [primaryItem]);
  const currentLayer = showAllProperties ? (layerProperties[focusedIndex] || null) : null;

  // ── Navigation helpers ────────────────────────────────────────────────────
  const navigatePrev = useCallback(() => {
    if (navTotal === 0 || focusedIndex <= 0) {
      const prevTime = findAdjacentMediaTime(canvasElements, displayTime, 'prev', 25, showAllProperties);
      if (prevTime !== null) { pendingJumpRef.current = 'end'; setCurrentTime(prevTime); }
    } else { setFocusedIndex(focusedIndex - 1); }
    onNavigate?.();
  }, [navTotal, focusedIndex, canvasElements, displayTime, setCurrentTime, onNavigate, showAllProperties]);

  const navigateNext = useCallback(() => {
    if (navTotal === 0 || focusedIndex >= navTotal - 1) {
      const nextTime = findAdjacentMediaTime(canvasElements, displayTime, 'next', 25, showAllProperties);
      if (nextTime !== null) { pendingJumpRef.current = 'start'; setCurrentTime(nextTime); }
    } else { setFocusedIndex(focusedIndex + 1); }
    onNavigate?.();
  }, [navTotal, focusedIndex, canvasElements, displayTime, setCurrentTime, onNavigate, showAllProperties]);

  const handleThumbClick = useCallback((idx) => {
    if (navTotal === 0) return;
    setFocusedIndex(idx % navTotal);
    setEditingText(null);
  }, [navTotal]);

  // Keyboard navigation (both modes)
  useEffect(() => {
    const onKey = (e) => {
      if (editingText) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      e.key === 'ArrowLeft' ? navigatePrev() : navigateNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingText, navigatePrev, navigateNext]);

  
  // ── Element lookup ────────────────────────────────────────────────────────
  const findCanvasElement = useCallback(
    (elementId) => canvasElements.find((el) => el.id === elementId) || null,
    [canvasElements],
  );

  // ── Helper: expand to "show all edits" focused on a specific AI layer ───
  const expandToAiLayer = useCallback((item) => {
    // Find the matching layer index in layerProperties
    const layerIdx = layerProperties.findIndex(lp => lp.elementId === item.elementId);
    setShowAllProperties(true);
    setFocusedIndex(layerIdx >= 0 ? layerIdx : 0);
    setEditingText(null);
  }, [layerProperties]);

  // ── Simple-mode change handler ────────────────────────────────────────────
  const handleChangeClick = useCallback((item) => {
    if (!item) return;
    const canvasEl = findCanvasElement(item.elementId);
    if (!canvasEl) return;

    // AI items: expand to show all edits for that layer
    if (item.isAiGenerated) {
      expandToAiLayer(item);
      return;
    }

    if (item.category === 'text') {
      setEditingText({ item, draft: item.value || '' });
      return;
    }
    if (!['image', 'video', 'audio'].includes(item.category)) return;

    const mediaType = (item.kind === 'element' && item.originalType) ? item.originalType : item.category;

    if (item.kind === 'element' && replaceElementMedia) {
      const opts = buildStandaloneOpts(canvasEl);
      mediaSelectorRef.current?.open(mediaType, item.value, (result, newName) => {
        const url = typeof result === 'string' ? result : result?.url;
        const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
        if (url) replaceElementMedia(canvasEl.id, url, canvasEl.type, newName || canvasEl.name, thumbnailUrl);
      }, opts);
    } else {
      const opts = {};
      if (item.targetWidth) opts.targetWidth = item.targetWidth;
      if (item.targetHeight) opts.targetHeight = item.targetHeight;
      if (item.duration) opts.maxDuration = item.duration;
      mediaSelectorRef.current?.open(mediaType, item.value, (result) => {
        const url = typeof result === 'string' ? result : result?.url;
        const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
        if (url) {
          handlePropertyChange(`componentData.fields[${item.fieldIndex}].${item.fieldType}`, url, false, canvasEl);
          // Store the thumbnail alongside the video field so it can be used as a poster
          if (thumbnailUrl && item.category === 'video') {
            handlePropertyChange(`componentData.fields[${item.fieldIndex}].thumbnail`, thumbnailUrl, false, canvasEl);
          }
        }
      }, opts);
    }
  }, [findCanvasElement, handlePropertyChange, replaceElementMedia, expandToAiLayer]);

  // ── Expanded-mode handlers ────────────────────────────────────────────────
  const handleExpandedMediaClick = useCallback((layer, field) => {
    if (!layer?.element) return;
    const canvasEl = layer.element;
    const mediaType = (field.kind === 'element' && field.originalType) ? field.originalType : field.category;
    const opts = {};
    if (field.targetWidth) opts.targetWidth = field.targetWidth;
    if (field.targetHeight) opts.targetHeight = field.targetHeight;
    if (field.duration) opts.maxDuration = field.duration;

    if (field.kind === 'element' && replaceElementMedia) {
      mediaSelectorRef.current?.open(mediaType, field.value, (result, newName) => {
        const url = typeof result === 'string' ? result : result?.url;
        const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
        if (url) replaceElementMedia(canvasEl.id, url, canvasEl.type, newName || canvasEl.name, thumbnailUrl);
      }, opts);
    } else if (field.kind === 'field') {
      mediaSelectorRef.current?.open(mediaType, field.value, (result) => {
        const url = typeof result === 'string' ? result : result?.url;
        const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
        if (url) {
          handlePropertyChange(`componentData.fields[${field.fieldIndex}].${field.fieldType}`, url, false, canvasEl);
          // Store the thumbnail alongside the video field so it can be used as a poster
          if (thumbnailUrl && field.category === 'video') {
            handlePropertyChange(`componentData.fields[${field.fieldIndex}].thumbnail`, thumbnailUrl, false, canvasEl);
          }
        }
      }, opts);
    }
  }, [handlePropertyChange, replaceElementMedia]);

  const handleExpandedFieldChange = useCallback((layer, field, value) => {
    if (!layer?.element) return;
    const canvasEl = layer.element;

    if (field.originalType === 'caption') {
      handleCaptionChange(value, canvasEl);
    } else if (field.kind === 'element' && field.originalType === 'text') {
      handlePropertyChange('componentData.text', value, false, canvasEl);
      if (canvasEl.pixiSprite) {
        canvasEl.pixiSprite.text = value;
        if (canvasEl.pixiSprite.interactiveArea) {
          const textWidth = canvasEl.pixiSprite.width;
          const textHeight = canvasEl.pixiSprite.height;
          canvasEl.pixiSprite.interactiveArea.clear();
          canvasEl.pixiSprite.interactiveArea.rect(-textWidth / 2, -textHeight / 2, textWidth, textHeight);
          canvasEl.pixiSprite.interactiveArea.fill(0x000000, 0);
        }
      }
    } else if (field.kind === 'field') {
      handlePropertyChange(`componentData.fields[${field.fieldIndex}].${field.fieldType}`, value, false, canvasEl);
    }
  }, [handlePropertyChange]);

  const handleControlChange = useCallback((layer, property, value) => {
    if (!layer?.element) return;
    handlePropertyChange(property, value, false, layer.element);
  }, [handlePropertyChange]);

  // ── Caption text updating ─────────────────────────────────────────────────
  const handleCaptionChange = useCallback((newFullText, canvasEl) => {
    const currentCaptions = canvasEl.componentData?.captions;
    if (!currentCaptions) return;

    const previousPhrases = currentCaptions.phrases || [];
    if (previousPhrases.length === 0) return;

    // Distribute time based on previous phrase length
    const oldWords = [];
    previousPhrases.forEach(p => {
      const pWords = p.text.split(/\s+/).filter(Boolean);
      const wDuration = pWords.length > 0 ? (p.end - p.start) / pWords.length : 0;
      pWords.forEach((w, i) => {
        oldWords.push({ start: p.start + i * wDuration, end: p.start + (i + 1) * wDuration });
      });
    });

    const newWords = newFullText.split(/\s+/).filter(Boolean);
    if (!newWords.length) {
      handlePropertyChange('componentData.captions', { ...currentCaptions, phrases: [] }, false, canvasEl);
      return;
    }

    const updatedPhrases = [];
    let currentPhrase = [];

    newWords.forEach((nw, i) => {
      let start, end;
      if (oldWords.length === 0) {
        start = i * 500; end = (i + 1) * 500;
      } else if (newWords.length === oldWords.length) {
        start = oldWords[i].start; end = oldWords[i].end;
      } else {
        const prevPercent = i / newWords.length;
        const nextPercent = (i + 1) / newWords.length;

        const getInterpolatedTime = (percent) => {
          const exactIndex = percent * oldWords.length;
          const floorIndex = Math.floor(exactIndex);
          const ceilIndex = Math.min(oldWords.length - 1, Math.ceil(exactIndex));
          const remainder = exactIndex - floorIndex;

          const floorTime = floorIndex < oldWords.length ? oldWords[floorIndex].start : oldWords[oldWords.length - 1].end;
          const ceilTime = ceilIndex < oldWords.length ? oldWords[ceilIndex].start : oldWords[oldWords.length - 1].end;

          return floorTime + remainder * (ceilTime - floorTime);
        };

        start = getInterpolatedTime(prevPercent);
        end = getInterpolatedTime(nextPercent);
      }

      currentPhrase.push({ text: nw, start, end });

      // Break phrases
      if (currentPhrase.length >= 3 || nw.match(/[.!?]$/) || i === newWords.length - 1) {
        updatedPhrases.push({
          start: currentPhrase[0].start,
          end: currentPhrase[currentPhrase.length - 1].end,
          text: currentPhrase.map(w => w.text).join(' ')
        });
        currentPhrase = [];
      }
    });

    handlePropertyChange('componentData.captions', { ...currentCaptions, phrases: updatedPhrases }, false, canvasEl);

    // If it has a PIXI sprite, also update its text right now if it's currently showing
    if (canvasEl.captionSprite) {
      // (This will simply refresh on the next render loop, but we can set text if appropriate)
      // Actually, since caption display relies on the timeline, updating the componentData is enough
      // to trigger a re-render in `LiteViewPage`'s `updateProgress` loop or `updateElement`.
    }
  }, [handlePropertyChange]);

  // ── Text editing ──────────────────────────────────────────────────────────
  const handleTextConfirm = useCallback(() => {
    if (!editingText) return;
    const { item, draft } = editingText;
    const canvasEl = findCanvasElement(item.elementId);
    if (canvasEl) {
      if (item.originalType === 'caption') {
        handleCaptionChange(draft, canvasEl);
      } else if (item.kind === 'element' && item.originalType === 'text') {
        const path = 'componentData.text';
        handlePropertyChange(path, draft, false, canvasEl);

        // Immediately sync to the PIXI sprite for visual update
        if (canvasEl.pixiSprite) {
          canvasEl.pixiSprite.text = draft;
          
          // Update the interactive area to match the new text dimensions
          if (canvasEl.pixiSprite.interactiveArea) {
            const textWidth = canvasEl.pixiSprite.width;
            const textHeight = canvasEl.pixiSprite.height;
            canvasEl.pixiSprite.interactiveArea.clear();
            canvasEl.pixiSprite.interactiveArea.rect(-textWidth / 2, -textHeight / 2, textWidth, textHeight);
            canvasEl.pixiSprite.interactiveArea.fill(0x000000, 0);
          }
        }
      } else {
        const path = item.kind === 'field'
          ? `componentData.fields[${item.fieldIndex}].${item.fieldType}`
          : 'text';
        handlePropertyChange(path, draft, false, canvasEl);
      }
    }
    setEditingText(null);
  }, [editingText, findCanvasElement, handlePropertyChange]);

  const handleTextCancel = useCallback(() => setEditingText(null), []);

  const toggleShowAll = useCallback(() => {
    setShowAllProperties((p) => !p);
    setFocusedIndex(0);
    setEditingText(null);
  }, []);

  // ── Empty state ───────────────────────────────────────────────────────────
  if (total === 0 && layerProperties.length === 0) {
    return (
      <div className="w-full sm:w-[85%] md:w-[70%] lg:w-[60%] bg-ag-black-800 px-3 sm:px-5 pt-2.5 pb-6 mb-4 flex-shrink-0 select-none items-center justify-center mx-auto rounded-t-2xl" style={{ minHeight: '25vh' }}>
        <div className="flex items-center justify-center py-6">
          <p className="text-gray-500 text-sm italic">No editable fields at this point in the timeline</p>
        </div>
        <KeyboardHint />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // EXPANDED VIEW
  // ══════════════════════════════════════════════════════════════════════════
  // Determine whether prev/next navigation is possible (index + adjacent time segments)
  const canGoPrev = focusedIndex > 0 || findAdjacentMediaTime(canvasElements, displayTime, 'prev', 25, showAllProperties) !== null;
  const canGoNext = focusedIndex < navTotal - 1 || findAdjacentMediaTime(canvasElements, displayTime, 'next', 25, showAllProperties) !== null;

  if (showAllProperties) {
    return (
      <ExpandedView
        expandedContainerRef={expandedContainerRef}
        currentLayer={currentLayer}
        thumbPct={thumbPct}
        topPct={topPct}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        navigatePrev={navigatePrev}
        navigateNext={navigateNext}
        handleExpandedFieldChange={handleExpandedFieldChange}
        handleExpandedMediaClick={handleExpandedMediaClick}
        handlePropertyChange={handlePropertyChange}
        handleControlChange={handleControlChange}
        handleSliderStart={handleSliderStart}
        handleSliderEnd={handleSliderEnd}
        openFontFieldIndex={openFontFieldIndex}
        onOpenFontField={setOpenFontFieldIndex}
        toggleShowAll={toggleShowAll}
        mediaSelectorRef={mediaSelectorRef}
      />
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SIMPLE VIEW
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <SimpleView
      editingText={editingText}
      setEditingText={setEditingText}
      handleTextConfirm={handleTextConfirm}
      handleTextCancel={handleTextCancel}
      questionText={questionText}
      primaryItem={primaryItem}
      secondaryItems={secondaryItems}
      thumbPct={thumbPct}
      topPct={topPct}
      canGoPrev={canGoPrev}
      canGoNext={canGoNext}
      navigatePrev={navigatePrev}
      navigateNext={navigateNext}
      handleChangeClick={handleChangeClick}
      handleThumbClick={handleThumbClick}
      toggleShowAll={toggleShowAll}
      mediaSelectorRef={mediaSelectorRef}
    />
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 * ExpandedView — "Show all properties" layout
 * ═══════════════════════════════════════════════════════════════════════════ */
const ExpandedView = ({
  expandedContainerRef, currentLayer, thumbPct, topPct,
  canGoPrev, canGoNext, navigatePrev, navigateNext,
  handleExpandedFieldChange, handleExpandedMediaClick, handlePropertyChange,
  handleControlChange, handleSliderStart, handleSliderEnd,
  openFontFieldIndex, onOpenFontField,
  toggleShowAll, mediaSelectorRef,
}) => (
  <div ref={expandedContainerRef} className="w-[90%] sm:w-[85%] md:w-[70%] lg:w-[60%] bg-ag-black-800 px-3 sm:px-5 pt-2.5 pb-6 mb-4 flex-shrink-0 select-none mx-auto rounded-t-2xl overflow-y-auto" style={{ height: '35vh', scrollbarWidth: 'thin', scrollbarColor: '#4A4A4A #1F1E1E' }}>
    {currentLayer && (
      <p className="w-[50%] text-ag-black-300 font-sourceSans italic text-sm sm:text-lg mb-2 sm:mb-3 truncate">{currentLayer.elementName}</p>
    )}

    {/* Main row: ← arrow | content | → arrow */}
    <div className="flex items-stretch gap-2 sm:gap-3">
      {/* Left arrow */}
      <button
        onClick={navigatePrev}
        disabled={!canGoPrev}
        className={`flex-shrink-0 flex items-center justify-center w-8 sm:w-10 rounded-xl transition-colors ${canGoPrev ? 'bg-ag-black-700 hover:bg-ag-black-600 text-ag-green cursor-pointer' : 'bg-transparent text-ag-black-400 cursor-default'}`}
        title="Previous layer"
      >
        <ChevronLeft size={20} />
      </button>

        {/* Center content */}
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch">
          {/* Fields */}
          {currentLayer && (currentLayer.isAiLayer || currentLayer.fields.length > 0) && (
            <div className="flex-1 min-w-0 bg-ag-black-700 rounded-2xl border border-ag-black-600 p-3 sm:p-4">
              <span className="inline-block text-xs font-medium text-ag-green border border-ag-black-400 rounded-full px-3 py-0.5 mb-2 sm:mb-3">Fields</span>
              {currentLayer.isAiLayer ? (
                <AiLiteFields layer={currentLayer} />
              ) : (
                <ExpandedFields
                  layer={currentLayer}
                  onFieldChange={handleExpandedFieldChange}
                  onMediaClick={handleExpandedMediaClick}
                  onPropertyChange={handlePropertyChange}
                  openFontFieldIndex={openFontFieldIndex}
                  onOpenFontField={onOpenFontField}
                />
              )}
            </div>
          )}

          {/* Controls */}
          {currentLayer && (
            <div className="w-full sm:w-[240px] flex-shrink-0 bg-ag-black-700 rounded-2xl border border-ag-black-600 p-3 sm:p-4">
              <span className="inline-block text-xs font-medium text-ag-green border border-ag-black-400 rounded-full px-3 py-0.5 mb-2 sm:mb-3">Controls</span>
              <ExpandedControls layer={currentLayer} onControlChange={handleControlChange} onSliderStart={handleSliderStart} onSliderEnd={handleSliderEnd} />
            </div>
          )}
        </div>

        {/* Right arrow */}
      <button
        onClick={navigateNext}
        disabled={!canGoNext}
        className={`flex-shrink-0 flex items-center justify-center w-8 sm:w-10 rounded-xl transition-colors ${canGoNext ? 'bg-ag-black-700 hover:bg-ag-black-600 text-ag-green cursor-pointer' : 'bg-transparent text-ag-black-400 cursor-default'}`}
        title="Next layer"
      >
        <ChevronRight size={20} />
      </button>
    </div>

    {/* Show less button — centered on mobile */}
    <div className="flex justify-center sm:justify-end mt-3">
      <button className="bg-ag-black-700 text-ag-black-300 text-sm font-sourceSans rounded-lg px-4 py-2 cursor-pointer hover:text-white hover:bg-ag-black-600 transition-all whitespace-nowrap border border-ag-black-400" onClick={toggleShowAll}>Show less edits</button>
    </div>
    <MediaSelectorModal ref={mediaSelectorRef} />
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
 * SimpleView — default compact layout
 * ═══════════════════════════════════════════════════════════════════════════ */
const SimpleView = ({
  editingText, setEditingText, handleTextConfirm, handleTextCancel,
  questionText, primaryItem, secondaryItems, thumbPct, topPct,
  canGoPrev, canGoNext, navigatePrev, navigateNext,
  handleChangeClick, handleThumbClick, toggleShowAll, mediaSelectorRef,
}) => (
  <div className="w-full sm:w-[85%] md:w-[70%] lg:w-[60%] bg-ag-black-800 px-3 sm:px-5 pt-2.5 pb-6 sm:pb-12 mb-4 flex-shrink-0 select-none mx-auto rounded-t-2xl" style={{ minHeight: '25vh' }}>
    {/* Desktop layout: horizontal row */}
    <div className="hidden sm:flex items-stretch justify-center rounded-2xl px-4 py-3 w-full" style={{ minHeight: '20vh' }}>
      {/* Progress bar */}
      <div className="w-1 self-stretch rounded-full bg-ag-black-600 flex-shrink-0 min-h-[50%] relative overflow-hidden">
        <div className="absolute left-0 w-full rounded-full bg-ag-green transition-all duration-300 ease-out" style={{ height: `${thumbPct}%`, top: `${topPct}%` }} />
      </div>

      {/* Question / text editor */}
      <div className="max-w-[55%] lg:max-w-[45%] mx-3 lg:mx-4 flex items-center justify-center gap-3 flex-1 min-w-0 bg-ag-black-700 rounded-2xl border border-ag-black-600 overflow-hidden">
        {editingText ? (
          <div className="flex flex-col gap-2 w-full px-3 lg:px-4 py-3">
            <textarea
              className="w-full bg-ag-black-900 text-white text-sm rounded-lg border border-ag-black-400 focus:border-ag-green focus:outline-none p-2 resize-none"
              rows={3}
              value={editingText.draft}
              onChange={(e) => setEditingText((p) => ({ ...p, draft: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTextConfirm(); }
                else if (e.key === 'Escape') handleTextCancel();
              }}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button className="px-3 py-1 text-xs rounded bg-ag-black-600 text-gray-300 hover:text-white transition-colors" onClick={handleTextCancel}>Cancel</button>
              <button className="px-3 py-1 text-xs rounded bg-ag-green text-black font-medium hover:brightness-110 transition-all" onClick={handleTextConfirm}>Save</button>
            </div>
          </div>
        ) : (
          <p className="text-white font-sourceSans font-light italic font-tighter text-xl md:text-2xl lg:text-4xl px-3 lg:px-4 py-2 break-words overflow-hidden">
            {questionText.line1}<br />{questionText.line2}
          </p>
        )}
      </div>

      {/* Primary thumbnail */}
      <div className="flex flex-col items-center gap-1.5 flex-shrink-0 justify-end">
        <ItemThumb item={primaryItem} large highlighted onClick={() => handleChangeClick(primaryItem)} />
        <button className="bg-ag-black-700 text-ag-green text-xs font-sourceSans rounded px-3.5 cursor-pointer hover:brightness-110 transition-all whitespace-nowrap" onClick={() => handleChangeClick(primaryItem)}>
          {primaryItem?.isAiGenerated ? 'edit' : 'change'}
        </button>
      </div>

      {/* Secondary thumbnails + show all */}
      {secondaryItems.length > 0 && (
        <div className="flex flex-col items-start gap-1.5 pl-3 lg:pl-4 flex-shrink-0 justify-end">
          <div className="flex gap-1.5 items-end">
            {secondaryItems.map((item) => (
              <ItemThumb key={`${item.elementId}-${item.fieldIndex ?? 'el'}-${item._originalIdx}`} item={item} onClick={() => { handleThumbClick(item._originalIdx); handleChangeClick(item); }} />
            ))}
          </div>
          <button className="text-gray-400 text-xs bg-transparent border-none cursor-pointer hover:text-ag-green transition-colors whitespace-nowrap p-0" onClick={toggleShowAll}>Show all edits</button>
        </div>
      )}
    </div>

    {/* Mobile layout: stacked vertical with nav arrows */}
    <div className="flex sm:hidden flex-col gap-3 px-2 py-3 h-full">
      {/* Navigation arrows + Question text + primary thumbnail row */}
      <div className="flex items-center gap-2 flex-1 min-h-0">
        {/* Left arrow */}
        <button
          onClick={navigatePrev}
          disabled={!canGoPrev}
          className={`flex-shrink-0 flex items-center justify-center w-8 h-full rounded-xl transition-colors ${canGoPrev ? 'bg-ag-black-700 hover:bg-ag-black-600 text-ag-green cursor-pointer' : 'bg-transparent text-ag-black-400 cursor-default'}`}
        >
          <ChevronLeft size={18} />
        </button>

        {/* Question / text editor */}
        <div className="flex-1 min-w-0 flex items-center bg-ag-black-700 rounded-2xl border border-ag-black-600 p-3 self-stretch">
          {editingText ? (
            <div className="flex flex-col gap-2 w-full">
              <textarea
                className="w-full bg-ag-black-900 text-white text-sm rounded-lg border border-ag-black-400 focus:border-ag-green focus:outline-none p-2 resize-none"
                rows={2}
                value={editingText.draft}
                onChange={(e) => setEditingText((p) => ({ ...p, draft: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTextConfirm(); }
                  else if (e.key === 'Escape') handleTextCancel();
                }}
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button className="px-3 py-1 text-xs rounded bg-ag-black-600 text-gray-300 hover:text-white transition-colors" onClick={handleTextCancel}>Cancel</button>
                <button className="px-3 py-1 text-xs rounded bg-ag-green text-black font-medium hover:brightness-110 transition-all" onClick={handleTextConfirm}>Save</button>
              </div>
            </div>
          ) : (
            <p className="text-white font-sourceSans font-light italic text-xl leading-tight">
              {questionText.line1}<br />{questionText.line2}
            </p>
          )}
        </div>

        {/* Primary thumbnail + change button */}
        <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
          <ItemThumb item={primaryItem} large highlighted onClick={() => handleChangeClick(primaryItem)} />
          <button className="bg-ag-black-700 text-ag-green text-xs font-sourceSans rounded px-3.5 cursor-pointer hover:brightness-110 transition-all whitespace-nowrap" onClick={() => handleChangeClick(primaryItem)}>
            {primaryItem?.isAiGenerated ? 'edit' : 'change'}
          </button>
        </div>

        {/* Right arrow */}
        <button
          onClick={navigateNext}
          disabled={!canGoNext}
          className={`flex-shrink-0 flex items-center justify-center w-8 h-full rounded-xl transition-colors ${canGoNext ? 'bg-ag-black-700 hover:bg-ag-black-600 text-ag-green cursor-pointer' : 'bg-transparent text-ag-black-400 cursor-default'}`}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Autograph Assistant label + Show all edits */}
      <div className="flex items-center justify-between px-1">
        <span className="text-ag-black-300 text-xs italic font-sourceSans">Autograph Assistant</span>
        <button className="text-gray-400 text-xs bg-transparent border-none cursor-pointer hover:text-ag-green transition-colors whitespace-nowrap p-0" onClick={toggleShowAll}>Show all edits</button>
      </div>
    </div>

    <div className="hidden sm:block">
      <KeyboardHint onPrev={navigatePrev} onNext={navigateNext} />
    </div>
    <MediaSelectorModal ref={mediaSelectorRef} />
  </div>
);

export default LitePropertiesPanel;
